import { Stack, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import { Routes } from '~/domains/common/constants/routes';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';

import { WithdrawalCompleted } from './WithdrawalCompleted';
import WithdrawalForm from './WithdrawalForm';
import useWithdrawalAmountHandoff from './hooks/useWithdrawalAmountHandoff';
import useWithdrawalFormInitialization from './hooks/useWithdrawalFormInitialization';
import { WithdrawalFormProvider } from './context/withdrawal.context';
import { PaymentMethodsSkeleton } from './components/PaymentMethodsSkeleton';

const useStyles = createStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  loadingContent: {
    flex: 1,
  },
}));

type WithdrawalViewProps = {
  onContinue?: () => void;
};

function WithdrawalView({ onContinue }: WithdrawalViewProps) {
  const { t } = useTranslation('payments');
  const { classes } = useStyles();
  const isAeropayEnabled = useIsFeatureEnabled(FEATURE.ENABLE_AEROPAY);

  const preservedAmount = useWithdrawalAmountHandoff();

  const [withdrawalMode, setWithdrawalMode] = useState<'withdrawal' | 'refund'>('withdrawal');
  const form = useWithdrawalFormInitialization(
    false,
    preservedAmount === undefined ? undefined : { withdrawalAmount: preservedAmount }
  );

  // Keep form mode in sync with our state
  useEffect(() => {
    if (form.values.mode !== withdrawalMode) {
      form.setFieldValue('mode', withdrawalMode);
    }
  }, [withdrawalMode, form.setFieldValue, form.values.mode]);

  const { isLoading: isProtectionLoading, isAccessible } = useProtectedGuard({
    action: 'make a withdrawal',
    protectionLevels: [ProtectionGuardType.KYC, ProtectionGuardType.LOCATION],
    checkOnMount: true,
    redirectOnClose: Routes.home,
    requireKycReturn: true,
  });

  return (
    <WithdrawalFormProvider form={form}>
      {form.values.completedWithdrawalAmount && (
        <WithdrawalCompleted
          withdrawalAmount={form.values.completedWithdrawalAmount}
          onClose={onContinue}
          paymentType={form.values.paymentType}
        />
      )}
      {!form.values.completedWithdrawalAmount && (
        <Stack
          /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8}
          maw={450}
          pos="relative"
          mx="auto"
          mih={790}
          className={classes.container}
        >
          {/* Show shared loading skeleton during protection checks or when access is denied */}
          {isProtectionLoading || !isAccessible ? (
            <Stack
              /* eslint-disable-line local-rules/use-theme-tokens */ spacing={40}
              className={classes.loadingContent}
            >
              <SignUpHeader
                title={t('withdrawal.form.select.title')}
                copy={t('withdrawal.form.select.copy')}
                isLogoVisible={false}
              />
              <PaymentMethodsSkeleton isAeropayEnabled={isAeropayEnabled} />
            </Stack>
          ) : (
            <WithdrawalForm
              onModalClose={onContinue}
              mode={withdrawalMode}
              onModeChange={setWithdrawalMode}
            />
          )}
        </Stack>
      )}
    </WithdrawalFormProvider>
  );
}

export default WithdrawalView;
